import React from 'react';
import classNames from 'classnames';
import { Slider, SliderProps } from '@easyv/react-components';
import { isNil, isNumber } from '@/utils';
import { ProInputNumber } from './ProInputNumber';
import { formContentLWidth1, formContentRWidth1 } from '@/constants';

export interface ProInputSliderProp extends Omit<SliderProps, 'onChange'> {
  name: string;
  value: number;
  showInput?: boolean;
  limitSliderOnly?: boolean;
  suffix?: string;
  prefix?: string;
  className?: string;
  precisionOnShow?: number;
  precisionOnSave?: number;
  onChange?: (name: string, value: number) => void;
  onBlur: (name: string, value: number) => void;
  inputToRealValue?: (value: number) => number;
  realToInputValue?: (value: number) => number;
}

interface TimeValue {
  hours: number;
  minutes: number;
}

function convertHourToTime(hours: number): TimeValue {
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  return {
    hours: wholeHours,
    minutes: minutes >= 60 ? 59 : minutes,
  };
}

function convertTimeToHour(hours: number, minutes: number): number {
  return hours + minutes / 60;
}

export function ProInputSliderForTime({
  name,
  min,
  max,
  limitSliderOnly = true,
  value,
  step = 0.01,
  showInput = true,
  suffix = '',
  prefix = '',
  className,
  precisionOnShow = 2,
  precisionOnSave,
  onChange,
  onBlur,
  disabled,
  inputToRealValue,
  realToInputValue,
  ...props
}: ProInputSliderProp) {
  const [innerValue, setInnerValue] = React.useState<number>(0);
  const { hours, minutes } = convertHourToTime(innerValue);

  const [timeInputs, setTimeInputs] = React.useState<TimeValue>({
    hours: hours,
    minutes: minutes,
  });

  React.useEffect(() => {
    setInnerValue(realToInputValue ? realToInputValue(value || 0) : value || 0);
    setTimeInputs(convertHourToTime(value));
  }, [realToInputValue, value]);

  const handleSliderChange = (name: string, e: number | number[] | undefined) => {
    const value: number = isNil(e) ? 0 : typeof e === 'number' ? e : e[0];
    const saveValue = inputToRealValue ? inputToRealValue(value) : value;
    setInnerValue(value);
    setTimeInputs(convertHourToTime(value));
    onChange?.(name, saveValue);
  };

  const handleSliderBlur = (name: string, e: number | number[] | undefined) => {
    const value: number = isNil(e) ? 0 : typeof e === 'number' ? e : e[0];
    const saveValue = inputToRealValue ? inputToRealValue(value) : value;
    setInnerValue(value);
    setTimeInputs(convertHourToTime(value));
    onBlur?.(name, saveValue);
  };

  const handleHoursChange = (name: string, e: number | number[] | undefined) => {
    const value: number = isNil(e) ? 0 : typeof e === 'number' ? e : e[0];
    setTimeInputs({ ...timeInputs, hours: value });
    const saveValue = convertTimeToHour(value, timeInputs.minutes);
    setInnerValue(saveValue);
    onChange?.(name, saveValue);
  };

  const handleMinutesChange = (name: string, e: number | number[] | undefined) => {
    const value: number = isNil(e) ? 0 : typeof e === 'number' ? e : e[0];
    setTimeInputs({ ...timeInputs, minutes: value });
    const saveValue = convertTimeToHour(timeInputs.hours, value);
    setInnerValue(saveValue);
    onChange?.(name, saveValue);
  };

  const handleHoursBlur = (name: string, e: number | number[] | undefined) => {
    const value: number = isNil(e) ? 0 : typeof e === 'number' ? e : e[0];
    setTimeInputs({ ...timeInputs, hours: value });
    const saveValue = convertTimeToHour(value, timeInputs.minutes);
    setInnerValue(saveValue);
    onBlur?.(name, saveValue);
  };

  const handleMinutesBlur = (name: string, e: number | number[] | undefined) => {
    const value: number = isNil(e) ? 0 : typeof e === 'number' ? e : e[0];
    setTimeInputs({ ...timeInputs, minutes: value });
    const saveValue = convertTimeToHour(timeInputs.hours, value);
    setInnerValue(saveValue);
    onBlur?.(name, saveValue);
  };

  return (
    <div className='flex gap-x-2 items-center'>
      <Slider
        className={classNames(className)}
        style={showInput ? { width: formContentLWidth1 } : { flex: '1 1 0%' }}
        min={0}
        max={23.999}
        step={step}
        disabled={disabled}
        value={innerValue}
        onChange={(num) => {
          handleSliderChange?.(name, num as number);
        }}
        onAfterChange={(num) => {
          handleSliderBlur?.(name, num as number);
        }}
        {...props}
      />
      {showInput && (
        <div className='flex items-center gap-x-1' style={{ width: formContentRWidth1 }}>
          <ProInputNumber
            name={name}
            disabled={disabled}
            min={0}
            max={23}
            suffix={suffix}
            prefix={prefix}
            precisionOnShow={precisionOnShow}
            precisionOnSave={precisionOnSave}
            step={1}
            value={timeInputs.hours}
            onChange={handleHoursChange}
            onBlur={handleHoursBlur}
            inputToRealValue={inputToRealValue}
            realToInputValue={realToInputValue}
          />
          <ProInputNumber
            name={name}
            disabled={disabled}
            min={0}
            max={59}
            suffix={suffix}
            prefix={prefix}
            precisionOnShow={precisionOnShow}
            precisionOnSave={precisionOnSave}
            step={1}
            value={timeInputs.minutes}
            onChange={handleMinutesChange}
            onBlur={handleMinutesBlur}
            inputToRealValue={inputToRealValue}
            realToInputValue={realToInputValue}
          />
        </div>
      )}
    </div>
  );
}
